import React from "react";
import { useEffect, useState } from "react";
import Chart from "../../common/ApexChart";
import allApi from "../../../../api/allApi";
import Loader from "../../common/Loader";
import { useLocalization } from "../../localization/LocalizationContext";
import { useNetwork } from "../../common/networkContext";

function WiredTrafficExpansion() {
  const { localizationStrings } = useLocalization();
  const { selectedNetworkId, selectedLocation } = useNetwork();
  const networkId = selectedNetworkId?.id ?? 6001;
  const [trafficSeries, setTrafficSeries] = useState([]);
  const [trafficCategories, setTrafficCategories] = useState([]);
  const [totalTrafficTableData, setTotalTrafficTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trafficDuration, setTrafficDuration] = useState("8h");
  const [trafficNetworkId, setTrafficNetworkId] = useState("");
  const [networkIds, setNetworkIds] = useState([]);
  const [refreshing,setRefreshing] = useState(true);

  // useEffect(()=>{
  //  fetchNetworkIds();
  // },[]);

  useEffect(() => {
    getTraffic(trafficDuration,networkId);
  }, [trafficDuration,networkId]);

  const handleTrafficDurationChange = (newDuration) => {
    setTrafficDuration(newDuration);
  };

  // const fetchNetworkIds = async () => {
  //   try {
  //     const api = new allApi();
  //     const response = await api.getRequest("api/network/allNetwork");
  //     if (
  //       response &&
  //       response.status === "OK" &&
  //       Array.isArray(response.response)
  //     ) {
  //       setNetworkIds(response.response); 
  //     }
  //   } catch (error) {
  //     console.error("Error fetching network IDs:", error);
  //   }
  // };

  const getTraffic = async (newDuration,networkId) => {
    try {
      setLoading(true);
      setRefreshing(true);
      const api = new allApi();
      const trafficData = await api.getRequest(`api/wired/dashboard/traffic/${newDuration}?networkId=${networkId}`);

      if (!Array.isArray(trafficData) || trafficData.length === 0) {
        setTrafficSeries([]);
        setTrafficCategories([]);
        setTotalTrafficTableData([]);
        return;
      }

      const deviceMap = new Map();
      const xLabelSet = new Set();

      const isAggregatedByDay = newDuration === '7d' || newDuration === '30d';

      trafficData.forEach((device) => {
        const { device_id, data } = device;
        const labelMap = new Map();

        data.forEach((item) => {
          let timestamp = new Date(item.interval1).getTime();

          if (isAggregatedByDay) {
            const date = new Date(timestamp);
            date.setHours(0, 0, 0, 0);
            timestamp = date.getTime();
          }

          xLabelSet.add(timestamp);
          const bandwidthMB = (item.bandwidth / 1024).toFixed(2);
          labelMap.set(timestamp, bandwidthMB);
        });

        deviceMap.set(device_id, labelMap);
      });

      let sortedTimestamps = Array.from(xLabelSet).sort((a, b) => a - b);

      const series = Array.from(deviceMap.entries()).map(([deviceId, labelMap]) => {
        const data = sortedTimestamps.map((ts) => ({
          x: ts,
          y: Number(labelMap.get(ts) || 0.0),
        }));

        return { name: `Device ${deviceId}`, data };
      });

      setTrafficSeries(series);
      setTrafficCategories(sortedTimestamps);

      const trafficTable = sortedTimestamps.map((ts) => {
        let total = 0;
        for (const [, labelMap] of deviceMap) {
          total += Number(labelMap.get(ts) || 0);
        }
        return { x: ts, y: total.toFixed(2) };
      });

      setTotalTrafficTableData(trafficTable);
    } catch (error) {
      console.error("Error fetching traffic", error);
      setTrafficSeries([]);
      setTrafficCategories([]);
      setTotalTrafficTableData([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getTickAmount = (duration) => {
    switch (duration) {
      case '8h': return 8;
      case '24h': return 12;
      case '7d': return 6;
      case '30d': return 10;
      default: return undefined;
    }
  };

  const trafficOptions = {
    chart: {
      type: "line",
      height: 135,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    xaxis: {
      type: "datetime",
      categories: trafficCategories,
      tickAmount: getTickAmount(trafficDuration),
      labels: {
        formatter: (value) => {
          const date = new Date(value);
          const options = (trafficDuration === '7d' || trafficDuration === '30d')
            ? { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' }
            : { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata', hour12: false };
          return date.toLocaleString('en-IN', options);
        },
        style: {
          colors: '#9aa0ac',
          fontSize: '12px',
        },
      },
      axisBorder: {
        show: true,
        color: '#d6d6d6',
      },
      axisTicks: {
        show: false,
      },
      tooltip: {
        enabled: true,
        formatter: (value) => {
          const date = new Date(value);
          const options = (trafficDuration === '7d' || trafficDuration === '30d')
            ? { day: '2-digit', month: 'short' }
            : { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata', hour12: false };
          return date.toLocaleString('en-IN', options);
        },
      },
    },
    yaxis: {
      min: 0,
      labels: {
        formatter: (val) => (val !== undefined ? val.toFixed(2) : 'N/A'),
      },
      title: {
        text: 'Traffic (MB)',
      },
    },
    stroke: {
      width: 1,
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      show: true,
      labels: {
        colors: "#9aa0ac",
        fontSize: "12px",
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val) => `${val} MB`,
      },
    },
    colors: [
      "#fc7594",
      "#00aaff",
      "#7fba00",
      "#f39c12",
      "#8e44ad",
      "#e74c3c",
      "#3498db",
      "#1abc9c",
      "#9b59b6",
      "#2ecc71",
    ],
    grid: {
      borderColor: '#e7e7e7',
    },
  };

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="inc-card ch-100">
          <div className="inc-card-title">
            <h3>{localizationStrings?.traffic}</h3>
            {/* <div className="dropdown">
                  <span
                    className="dropdown-toggle"
                    data-bs-toggle="dropdown"
                    style={{ cursor: "pointer" }}
                  >
                    {trafficNetworkId ? `Network: ${trafficNetworkId}` : "Select Network"}
                  </span>
                  <ul className="dropdown-menu">
                    {networkIds.map((net) => (
                      <li key={net.network_id}>
                        <button className="dropdown-item" onClick={() => setTrafficNetworkId(net.network_id)}>
                          {net.network_name || net.network_id}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div> */}
            <div className="inc-card-button">
              
              <span
                className="dropdown"
                id="clientDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <span>
                  {trafficDuration === "24h" ? "24h" : trafficDuration}
                  <i className="fa fa-angle-down" aria-hidden="true"></i>
                </span>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <a
                      className="dropdown-item"
                      onClick={() => handleTrafficDurationChange("8h")}
                    >
                      {localizationStrings?.last8Hours}
                    </a>
                  </li>
                  <li>
                    <a
                      className="dropdown-item"
                      onClick={() => handleTrafficDurationChange("24h")}
                    >
                      {localizationStrings?.last24Hours}
                    </a>
                  </li>
                  <li>
                    <a
                      className="dropdown-item"
                      onClick={() => handleTrafficDurationChange("7d")}
                    >
                      {localizationStrings?.last7Days}
                    </a>
                  </li>
                  <li>
                    <a
                      className="dropdown-item"
                      onClick={() => handleTrafficDurationChange("30d")}
                    >
                      {localizationStrings?.last30Days}
                    </a>
                  </li>
                </ul>
              </span>
              {/* <input className="list-search" type="text" /> */}
              <button className="icon-btn" title="Refresh" onClick={()=>getTraffic(trafficDuration,trafficNetworkId)}>
                {refreshing ? (
                  <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                ) : (
                  <i className="fa fa-refresh" aria-hidden="true"></i>
                )}
              </button>
              {/* <button className="icon-btn" title="Filter">
                <i className="fa fa-filter" aria-hidden="true"></i>
              </button> */}
            </div>
          </div>
          <div className="inc-card-body">
            <div className="row">
              <div className="col-lg-8">
                <div className="inc-card-body">
                  {loading ? (
                    <Loader />
                  ) : (
                    <div>
                      {trafficSeries.length === 0 ? (
                        <div className="no-data-box">
                          <div className="no-data-text">
                            {localizationStrings?.noData}
                          </div>
                        </div>
                      ) : (
                        <Chart
                          options={trafficOptions}
                          series={trafficSeries}
                          type="line"
                          height={250}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-lg-4">
                <div className="round-border">
                  <div className="inc-card-table">
                    <table className="m-head">
                      <thead>
                        <tr>
                          {/* <th>{localizationStrings?.devideId}</th> */}
                          <th>{localizationStrings?.date}</th>
                          <th>{localizationStrings?.totalBandwidth}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {totalTrafficTableData.length > 0 &&
                          totalTrafficTableData.map((item, index) => (
                            <tr key={index}>
                              <td>
                                {new Date(item.x).toLocaleString('en-IN',
                                  (trafficDuration === '7d' || trafficDuration === '30d')
                                    ? { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }
                                    : { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' }
                                )}
                              </td>
                              <td>{item.y}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WiredTrafficExpansion;
